import React from "react";
import { RhombusSplit, HomeCity, Phone } from "mdi-material-ui";
import { RobotDogIcon } from "orion-components/CBComponents/Icons";
import { mdiAccessPoint, mdiQuadcopter, mdiHome, mdiGoogleController } from "@mdi/js";
import { DashboardCustomize } from "@mui/icons-material";
import { SvgIcon } from "@mui/material";
import { getTranslation } from "orion-components/i18n";
import IconFrameOverlay from "orion-components/SharedComponents/IconFrameOverlay/IconFrameOverlay";
import moment from "moment";
import isNil from "lodash/isNil";

const jsonata = require("jsonata");

export const toTitleCase = str => {
	return str.replace(/\w\S*/g, txt => {
		return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
	});
};

export const getEventTypeIcon = (types, typeId) => {
	const normalizedTypeId = typeId?.toLowerCase();
	const type = types.find(item => item.id?.toLowerCase() === normalizedTypeId);
	return type?.icon ? (
		<IconFrameOverlay
			icon={type.icon}
			shape={"rounded_square"}
			color={type?.color}
			iconDimensions={{ width: "17.5px", height: "17.5px" }}
			customStyles={{ margin: "0 10px" }}
		/>
	) : null;
};

export const getIcon = (type, fontSize = "2rem", icon = null, types = [], typeId) => {
	if (!icon) {
		// Normalize the type to lowercase for consistent matching
		const normalizedType = type?.toLowerCase();

		switch (normalizedType) {
			case "track":
				icon = "directions_boat";
				break;
			case "point":
				icon = "place";
				break;
			case "polygon":
				icon = "layers";
				break;
			case "line":
				icon = "timeline";
				break;
			case "vehicle":
				icon = "directions_car";
				break;
			case "person":
				icon = "person";
				break;
			case "camera":
				icon = "videocam";
				break;
			case "planned":
			case "event":
			case "emergent":
				if (typeId) {
					return getEventTypeIcon(types, typeId);
				} else {
					icon = normalizedType === "emergent" ? "report_problem" : "event";
					break;
				}

			case "template":
				icon = <DashboardCustomize style={{ fontSize }} />;
				break;
			case "external_link":
				icon = "language";
				break;
			case "collection":
				icon = <RhombusSplit fontSize="large" />;
				break;
			case "facility":
				icon = <HomeCity fontSize="large" />;
				break;
			case "door":
			case "access point":
			case "device":
			case "compositedevice":
				icon = (
					<SvgIcon style={{ width: 34, height: 34 }}>
						<path d={mdiAccessPoint} />
					</SvgIcon>
				);
				break;
			case "zetron":
				icon = (
					<Phone
						style={{
							width: "24px",
							height: "24px",
							color: "#FFFFFF"
						}}
					/>
				);
				break;
			case "robot track":
			case "ghost robotics dog":
				icon = <RobotDogIcon />;
				break;
			case "drone":
				icon = (
					<SvgIcon style={{ width: 34, height: 34 }}>
						<path d={mdiQuadcopter} />
					</SvgIcon>
				);
				break;
			case "drone-controller":
				icon = (
					<SvgIcon style={{ width: 44, height: 44 }}>
						<path d={mdiGoogleController} />
					</SvgIcon>
				);
				break;
			case "drone-home":
				icon = (
					<SvgIcon style={{ width: 34, height: 34 }}>
						<path d={mdiHome} />
					</SvgIcon>
				);
				break;
			case "buoy":
				icon = <img alt="buoy" style={{ width: 34, height: 34 }} src={require("./Icons/buoy.png")} />;
				break;
			default:
				break;
		}
	}

	if (typeof icon === "string") {
		return (
			<i className="material-icons" style={{ fontSize, color: "#B5B9BE" }}>
				{icon}
			</i>
		);
	} else {
		return icon;
	}
};

export const getIconByTemplate = (
	type,
	entity,
	fontSize = "2rem",
	profileIconTemplate = null,
	eventTypes,
	eventTypeId
) => {
	let icon = null;

	if (profileIconTemplate && entity.entityData) {
		const expression = jsonata(profileIconTemplate);
		icon = expression.evaluate(entity.entityData);
	}
	return getIcon(type, fontSize, icon, eventTypes, eventTypeId);
};

export const getCalculatedDistance = (distance, unit, display) => {
	if (distance < 1 && unit === "kilometers") {
		const meters = distance * 1000;
		return meters.toFixed(2) + getTranslation("global.map.tools.distanceTool.m");
	} else if (distance < 1 && unit === "miles") {
		const feet = distance * 5280;
		return feet.toFixed(2) + getTranslation("global.map.tools.distanceTool.ft");
	} else {
		return distance.toFixed(2) + display;
	}
};

export const ShapeSelectorIconConfig = (iconName, customStyles = {}) => {
	switch (iconName) {
		case "point":
			return (
				<img
					alt={iconName}
					src={require(`./Icons/shapeSelector/${iconName}.png`)}
					style={{ ...customStyles }}
				/>
			);
		case "line":
			return (
				<img
					alt={iconName}
					src={require(`./Icons/shapeSelector/${iconName}.png`)}
					style={{ ...customStyles }}
				/>
			);
		case "polygon":
			return (
				<img
					alt={iconName}
					src={require(`./Icons/shapeSelector/${iconName}.png`)}
					style={{ ...customStyles }}
				/>
			);
		case "circle":
			return (
				<img
					alt={iconName}
					src={require(`./Icons/shapeSelector/${iconName}.png`)}
					style={{ ...customStyles }}
				/>
			);
		case "rectangle":
			return (
				<img
					alt={iconName}
					src={require(`./Icons/shapeSelector/${iconName}.png`)}
					style={{ ...customStyles }}
				/>
			);
		default:
			return null;
	}
};

// This function generates time options for a dropdown based on the provided interval, start, and end values. Please find an example of the arguments passed to this function below.
/*
config = {
timeFormat: 24-hour or 12-hour,
interval: 30 | 60 | 90 (In minutes),
start: 0 - 24,
end: 0 - 24,
locale: en | ar
}
*/
export const generateTimeOptions = (config = {}) => {
	const { timeFormat, interval = 30, start = 0, end = 24, locale = "en" } = config;
	const timeOptions = [];
	const format = timeFormat === "24-hour" ? "HH:mm" : "hh:mm A";
	const maxTime = moment(end, format);

	for (let hr = start; hr <= end; hr++) {
		for (let min = 0; min < 60; min += interval) {
			const hour = (hr % 24)?.toString()?.padStart(2, "0");
			const minute = min.toString().padStart(2, "0");

			const timeOption = hour && minute && `${hour}:${minute}`?.trim();
			const alreadyExists = timeOptions.find(option => option.value === timeOption);
			const localizedDate = moment(timeOption, format).locale(locale).format(format);

			if (timeOption && !alreadyExists && moment(timeOption, format) <= maxTime) {
				const option = { label: localizedDate, value: timeOption };
				timeOptions.push(option);
			}
		}
	}

	return timeOptions;
};

// This function generates duration options for a dropdown based on the provided min and max minute values. Currently, we haven't set any limit/range for the duration, so any possible duration can be specified. The interval has been adjusted to 15 minutes for the first hour and increased to 30 minutes for the rest of the hours.
export const generateDurationOptions = (minMinutes = 15, maxMinutes = 240) => {
	const durationOptions = [];
	let minutes = minMinutes;

	while (minutes <= maxMinutes) {
		const hrs = Math.floor(minutes / 60);
		const mins = minutes % 60;

		let label = "";
		if (hrs > 0) {
			label += `${hrs}hr${hrs > 1 ? "s" : ""}`;
		}
		if (mins > 0) {
			label += `${hrs > 0 ? "_" : ""}${mins}min`;
		}

		if (label && minutes) {
			durationOptions.push({
				label: label,
				value: minutes
			});
		}

		minutes += minutes < 60 ? 15 : 30;
	}

	return durationOptions;
};

// This method converts a dateTime or a time string into user's preferred time format. Please find possible inputs that could be provided to this function below.
/*
dateTime - 2025-04-17T10:00:00.000Z (full date-time) | 15:30 (24-hour string) | 03:30 PM (12-hour string)
timeFormat - 24-hour | 12-hour
locale - en | ar 
*/
export const convertToUserTime = (dateTime, timeFormat, locale = "en") => {
	if (!dateTime) return;
	const format = timeFormat === "24-hour" ? "HH:mm" : "hh:mm A";
	const dateTimeObject = moment(dateTime).isValid() ? moment(dateTime) : moment(dateTime, format);
	return dateTimeObject.locale(locale).format(format);
};
export const processAggregates = alertsInput => {
	// Return early for empty or invalid input
	if (!alertsInput || Object.keys(alertsInput).length === 0) {
		return Array.isArray(alertsInput) ? [] : {};
	}

	// Handle the case where the input is an object, not an array
	// This block of code handles the specific case where the input 'alertsInput' is an object.
	// The object is expected to have alert IDs as keys and alert objects as values.
	if (!Array.isArray(alertsInput)) {
		// Step 1: Extract all alert objects into an array for easier iteration.
		const alertsObjectValues = Object.values(alertsInput);

		// Step 2: Initialize data structures for processing.
		// 'idMap' will store a mutable copy of every alert for quick ID-based lookups.
		// 'childIds' will keep track of alerts that are nested under a parent,
		// so they can be excluded from the final top-level output.
		const idMap = new Map();
		const childIds = new Set();

		// Step 3: Populate the 'idMap' with a shallow copy of every alert.
		// Using a copy ({ ...alert }) is crucial because we will be modifying these objects
		// (e.g., adding a 'children' array) and we want to avoid mutating the original input.
		alertsObjectValues.forEach(alert => {
			idMap.set(alert.id, { ...alert });
		});

		// Step 4: Perform the aggregation. This loop identifies child alerts and nests them
		// inside their respective parents.
		alertsObjectValues.forEach(alert => {
			// Check if the current alert has an 'aggregateId', which points to its parent's ID.
			const { aggregateId = null } = alert;

			// Proceed only if the alert is a child (has an aggregateId) AND its parent exists in our map.
			if (aggregateId && idMap.has(aggregateId)) {
				// Retrieve the parent object from the map.
				const parent = idMap.get(aggregateId);

				// If this is the first child for this parent, initialize its 'children' array.
				if (!parent.children) {
					parent.children = [];
				}

				// Add the current alert object (from the map) to its parent's 'children' array.
				parent.children.push(idMap.get(alert.id));

				// Record this alert's ID in 'childIds' to mark it as a processed child.
				childIds.add(alert.id);
			}
		});

		// Step 5: Build the final output object containing only top-level alerts.
		const groupedObject = {};

		// Iterate through every alert we have processed in the 'idMap'.
		idMap.forEach((alert, id) => {
			// The core filtering logic: if an alert's ID is NOT in our set of childIds,
			// it means this is a top-level parent (or a standalone alert).
			if (!childIds.has(id)) {
				// Calculate a 'count' for the parent alert, which includes itself plus all its children.
				alert.count = alert.children ? alert.children.length + 1 : 1;

				// Add the fully processed parent alert to our final output object.
				groupedObject[id] = alert;
			}
		});

		// Step 6: Return the newly created object containing only the aggregated parent alerts.
		return groupedObject;
	} else {
		const alertsArray = alertsInput;
		const idMap = new Map();
		const childIds = new Set();

		// 1. First pass: Populate a map with clones of all alert items for quick lookup.
		// This map will be used to build the parent-child relationships.
		alertsArray.forEach(alert => {
			if (!alert.isGroupHeader) {
				idMap.set(alert.id, { ...alert });
			}
		});

		// 2. Second pass: Perform the aggregation.
		// Iterate over the map to link children to their parents.
		idMap.forEach(alert => {
			const { aggregateId } = alert;
			if (aggregateId && idMap.has(aggregateId)) {
				const parent = idMap.get(aggregateId);
				if (!parent.children) {
					parent.children = [];
				}
				parent.children.push(alert);
				childIds.add(alert.id);
			}
		});

		// 3. Third pass: Build the final result array.
		// Iterate through the original, sorted `alertsArray` to maintain its order.
		const finalGroupedArray = [];
		alertsArray.forEach(item => {
			if (item.isGroupHeader) {
				// If the item is a group header, add it directly to the result.
				finalGroupedArray.push(item);
			} else {
				// If the item is an alert, check if it's a top-level parent (i.e., not a child).
				if (!childIds.has(item.id)) {
					// Retrieve the processed alert from the map (it may now have children).
					const processedParentAlert = idMap.get(item.id);
					// Calculate the count of the parent plus all its children.
					processedParentAlert.count = processedParentAlert.children
						? processedParentAlert.children.length + 1
						: 1;
					finalGroupedArray.push(processedParentAlert);
				}
				// If the item is a child, do nothing. It has already been added to its parent's `children` array.
			}
		});

		return finalGroupedArray;
	}
};

/**
 * Converts a duration and unit to milliseconds.
 * @param {number} duration - Duration value.
 * @param {string} units - Unit ("m", "h", "d", "w").
 * @returns {number} Duration in milliseconds.
 * @throws {Error} If unit is invalid.
 */
export const getDelayFromDuration = (duration, units) => {
	let multiplier = 1000;
	switch (units) {
		case "m":
			multiplier = multiplier * 60;
			break;
		case "h":
			multiplier = multiplier * 60 * 60;
			break;
		case "d":
			multiplier = multiplier * 60 * 60 * 24;
			break;
		case "w":
			multiplier = multiplier * 60 * 60 * 24 * 7;
			break;
		default:
			return null;
	}
	return duration * multiplier;
};

export const downloadPDF = (data, filename) => {
	const byteArray = new Uint8Array(data);
	const blob = new Blob([byteArray], { type: "application/pdf" });
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = filename;
	link.click();
	window.URL.revokeObjectURL(url);
};
